import { Box } from '@mui/material'
import {
  getGridStringOperators,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridValueGetter,
} from '@mui/x-data-grid-premium'
import ChildCategoryCell from 'components/DataGrid/ChildRows/ChildCategoryCell'
import ChildContractedByCell from 'components/DataGrid/ChildRows/ChildContractedByCell'
import ChildDateCell from 'components/DataGrid/ChildRows/ChildDateCell'
import ChildNoValueCell from 'components/DataGrid/ChildRows/ChildNoValueCell'
// Prototype edit cells with auto-open and immediate commit behavior
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import {
  customDateSortOperator,
  customNumericSortOperator,
  customSortOperator,
  customTrackingSortOperator,
} from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { renderStringColumns } from 'components/DataGrid/operators/renderStringColumns'
import {
  ORG_STATUS_OPTIONS,
  ORG_TYPE_OPTIONS,
  TRACKING_TYPE_OPTIONS,
  VERTICAL_OPTIONS,
} from 'components/DataGrid/prototype/config/options'
import {
  contactEditCell,
  orgEditCell,
  selectEditCell,
  stringEditCell,
  userEditCell,
} from 'components/DataGrid/prototype/editCells'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import RenderOrganizationCell from 'components/MTB/grid/cells/RenderOrganizationCell'
import { Link } from 'react-router-dom'
import { Contact, DensityStyleTypes, Organization, OrganizationTableItem, User } from 'types'
import { formatDate } from 'utils/formatters/formatDates'
import { buildSearchIndex } from './buildSearchIndex'
import AdditionalContactsCell from './cells/AdditionalContactsCell'
import AffiliatedCell from './cells/AffiliatedCell'
import ContractedByCell from './cells/ContractedByCell'
import DateTimeCell from './cells/DateTimeCell'
import DiscoveryCell from './cells/DiscoveryCell'
import GoogleAdsCell from './cells/GoogleAdsCell'
import OrganizationStatusCell from './cells/OrganizationStatusCell'
import OwnerCell from './cells/OwnerCell'
import PrimaryContactCell from './cells/PrimaryContactCell'
import TeamMemberCell from './cells/TeamMemberCell'
import TrackingCell from './cells/TrackingCell'
import TypeCell from './cells/TypeCell'
import VerticalCell from './cells/VerticalCell'
import WebsiteCell from './cells/WebsiteCell'

// Options now imported from prototype/config/options.ts:
// ORG_STATUS_OPTIONS, ORG_TYPE_OPTIONS, VERTICAL_OPTIONS, TRACKING_TYPE_OPTIONS

// Props for editable columns - passed from parent component
export interface OrganizationGridColumnsOptions {
  users?: User[]
  contacts?: Contact[]
  organizations?: OrganizationTableItem[]
  parentOrganizations?: OrganizationTableItem[]
}

export const useOrganizationGridColumns = (options: OrganizationGridColumnsOptions = {}) => {
  const { users = [], contacts = [], organizations = [], parentOrganizations = [] } = options
  const handleEditClick = (params: GridRenderCellParams) => {
    params.api.startCellEditMode({
      id: params.id,
      field: params.field,
    })
  }
  const organizationGridColumns = (
    density: 'compact' | 'standard' | 'comfortable',
    handleRowClick: (row: GridRenderCellParams['row']) => void,
    toggleExpanded: (id: GridRowId) => void
  ): GridColDef[] => {
    const densityStyles: DensityStyleTypes = getDensityStyles(density)
    const columns: GridColDef[] = [
      {
        field: '__search',
        headerName: '',
        width: 1, // tiny
        minWidth: 1,
        sortable: false,
        filterable: true, // must be true so quick filter sees it
        disableColumnMenu: true,
        hideable: false, // keep users from hiding it
        pinnable: false,
        // resizable: false,
        // renderCell: () => null, // nothing to paint
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_v, row) => buildSearchIndex(row),
        headerClassName: 'sr-only',
        cellClassName: 'sr-only',
      },
      {
        field: 'org_uuid',
        headerName: 'Id',
        width: 100,
        valueGetter: params => {
          return params
        },
        renderCell: params => {
          return <Box>{params && params.value ? params.value : ''}</Box>
        },
      },
      {
        field: 'name',
        aggregable: false,
        pinnable: false,
        headerName: 'Organization Name',
        width: 250,
        // flex: 1,
        valueGetter: (_, row) => row?.name,
        renderCell: (params: GridRenderCellParams) => {
          // Server aggregation row - render simple text label
          if (params?.row?.isAggregationRow) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  padding: densityStyles.padding,
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {params?.row?.name || 'Total (All Filtered)'}
              </Box>
            )
          }
          return (
            <RenderOrganizationCell
              params={params}
              densityStyles={densityStyles}
              handleRowClick={handleRowClick}
              toggleExpanded={toggleExpanded}
            />
          )
        },
      },
      {
        field: 'discovery',
        headerName: 'Discovery',
        headerAlign: 'center',
        width: 50,
        align: 'center',
        valueGetter: (_value, row) => {
          if (!row) return 0
          const discoveryFields = [
            row.website,
            row.street,
            row.city,
            row.state,
            row.zip,
            row.cmsUsed,
            row.socialProfiles,
            row.social,
            row.marketingObjective,
            row.geoTargeting,
          ]
          const filledFieldsCount = discoveryFields.filter(field => !!field).length
          return row?.isParent
            ? filledFieldsCount ?? 0
            : !row?.isParent && row?.startDate
            ? formatDate(row?.startDate) ?? null
            : null
        },
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated || params?.row?.isAggregationRow) return null
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              {params?.row?.isParent ? (
                <Link
                  to={`/organizations/${params?.row?.PK}/details`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <DiscoveryCell params={params} densityStyles={densityStyles} />
                </Link>
              ) : (
                <ChildDateCell params={params} densityStyles={densityStyles} label="Start Date" />
              )}
            </Box>
          )
        },
      },
      {
        field: 'website',
        headerName: 'Website',
        headerAlign: 'left',
        editable: true,
        valueGetter: (_value, row) =>
          row?.isParent ? row?.website : !row?.isParent && row?.endDate ? formatDate(row?.endDate) : null,
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated || params?.row?.isAggregationRow) return null
          return <WebsiteCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: stringEditCell('url'),
      },
      {
        field: 'googleAdsId',
        headerName: 'Ads Id',
        headerAlign: 'left',
        editable: true,
        valueGetter: (_value, row) => (row?.googleAdsId ? row?.googleAdsId : null),
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated || params?.row?.isAggregationRow) return null
          return <GoogleAdsCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: stringEditCell('text'),
      },
      {
        field: 'total',
        headerName: 'Total',
        aggregable: true,
        type: 'number',
        width: 105,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.total ?? row?.total ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'monthlyTotal',
        headerName: 'Monthly',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.monthlyTotal ?? row?.monthlyTotal ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'spend',
        headerName: 'Spend',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.spend ?? row?.spend ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'grossProfit',
        headerName: 'Gross Profit',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.grossProfit ?? row?.grossProfit ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'totalGrossProfit',
        headerName: 'Total Gross',
        aggregable: true,
        type: 'number',
        width: 105,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.totalGrossProfit ?? row?.totalGrossProfit ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'implementationFee',
        headerName: 'Impl Fee',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.implementationFee ?? row?.implementationFee ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      // {
      //   field: 'website',
      //   headerName: 'Website',
      //   headerAlign: 'center',
      //   valueGetter: (_value, row): GridValueGetter => (row?.isParent ? row?.website : null),
      //   filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      //   sortComparator: customSortOperator,
      //   renderCell: (params: GridRenderCellParams) => {
      //     return <WebsiteCell params={params} densityStyles={densityStyles} />
      //   },
      // },
      {
        field: 'trackingType',
        headerName: 'Tracking Type',
        headerAlign: 'center',
        editable: true,
        width: 100,
        valueGetter: (_value, row): GridValueGetter => row.trackingType || '',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customTrackingSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <TrackingCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: selectEditCell(TRACKING_TYPE_OPTIONS),
      },
      {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'left',
        width: 75,
        editable: true,
        valueGetter: (_value, row): GridValueGetter => {
          return row?.status
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          const handleClick = () => {
            params.api.startCellEditMode({
              id: params.id,
              field: params.field,
            })
          }
          if (params?.row?.isAggregationRow) return null
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                cursor: 'pointer',
                px: 1,
              }}
              onClick={handleClick}
            >
              <OrganizationStatusCell params={params} densityStyles={densityStyles} />
            </Box>
          )
        },
        renderEditCell: selectEditCell(ORG_STATUS_OPTIONS),
      },
      //   {
      //     field: 'old_status',
      //     headerName: 'Old Status',
      //     headerAlign: 'left',
      //     valueGetter: (_value, row): GridValueGetter => {
      //       return row?.old_status
      //     },
      //     filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      //     sortComparator: customSortOperator,
      //   },
      {
        field: 'contractedBy',
        headerName: 'Contracted By',
        headerAlign: 'left',
        width: 100,
        editable: true,
        valueGetter: (_value, row) => {
          return row?.contractedBy?.length > 0
            ? row?.contractedBy.map((org: Organization) => org?.name).join(', ')
            : 'No Organizations'
        },
        valueSetter: (value, row) => {
          // Value comes as array of org_uuids, store directly
          return { ...row, contractedBy: value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return params?.row?.isParent ? (
            <ContractedByCell params={params} densityStyles={densityStyles} />
          ) : !params?.row?.isParent ? (
            <ChildContractedByCell params={params} densityStyles={densityStyles} />
          ) : null
        },
        renderEditCell: orgEditCell({ organizations: parentOrganizations }, true),
      },
      {
        field: 'affiliated',
        headerName: 'Affiliated',
        headerAlign: 'left',
        editable: true,
        width: 100,
        valueGetter: (_value, row) => {
          return row?.affiliated?.length > 0
            ? row?.affiliated.map((org: Organization) => org.name).join(', ')
            : 'No Organizations'
        },
        valueSetter: (value, row) => {
          return { ...row, affiliated: value }
        },
        sortComparator: customSortOperator,
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <AffiliatedCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: orgEditCell({ organizations: parentOrganizations }, true),
      },
      {
        field: 'type',
        headerName: 'Type',
        headerAlign: 'left',
        editable: true,
        width: 100,
        valueGetter: (_value, row): GridValueGetter => {
          const type = row?.isParent ? row?.type : !row?.isParent ? row?.category : null
          return type ?? ''
        },
        sortComparator: customSortOperator,
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          if (!params?.row?.isParent) return <ChildCategoryCell params={params} densityStyles={densityStyles} />
          return <TypeCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: selectEditCell(ORG_TYPE_OPTIONS),
      },
      {
        field: 'primaryContact',
        headerName: 'Primary Contact',
        headerAlign: 'left',
        editable: true,
        width: 100,
        valueGetter: (_value, row) => {
          return row?.primaryContact?.name ?? ''
        },
        valueSetter: (value, row) => {
          return { ...row, primaryContact: value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <PrimaryContactCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: contactEditCell({ contacts }, false),
      },
      {
        field: 'contacts',
        headerName: 'Addtl Contacts',
        width: 50,
        sortable: true,
        headerAlign: 'center',
        editable: true,
        valueGetter: (_value, row) => {
          const relatedContactNames =
            row?.relatedContacts?.length > 0
              ? row?.relatedContacts.map((contact: Contact) => contact.name).join(', ')
              : ''
          return relatedContactNames ?? ''
        },
        valueSetter: (value, row) => {
          return { ...row, contacts: value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <AdditionalContactsCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: contactEditCell({ contacts }, true),
      },
      {
        field: 'owner',
        headerName: 'Owner',
        width: 50,
        headerAlign: 'center',
        editable: true,
        valueGetter: (_value, row) => {
          return row?.owner?.name ?? ''
        },
        valueSetter: (value, row) => {
          return { ...row, owner: value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          if (!params?.row?.isParent) return <ChildNoValueCell densityStyles={densityStyles} />
          return <OwnerCell params={params} densityStyles={densityStyles} />
        },
        renderEditCell: userEditCell({ users }, false),
      },

      {
        field: 'vertical',
        headerName: 'Vertical',
        width: 100,
        headerAlign: 'center',
        sortable: false,
        editable: true,
        valueGetter: params => params,
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <VerticalCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        },
        renderEditCell: selectEditCell(VERTICAL_OPTIONS),
      },
      ...renderStringColumns(),
      {
        field: 'social',
        headerName: 'Social',
        width: 50,
        valueGetter: (_value, row): GridValueGetter => {
          return row?.social && row?.social.length > 0 ? row?.social?.map(params => params.network).join(',') : null
        },
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <Box>{params.value}</Box>
        },
      },
      {
        field: 'AM',
        headerName: 'AM',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['AM']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.AM': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'BDE',
        headerName: 'BDE',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['BDE']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.BDE': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'DMS',
        headerName: 'DMS',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['DMS']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.DMS': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'MIA',
        headerName: 'MIA',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['MIA']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.MIA': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'PM',
        headerName: 'PM',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['PM']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.PM': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'QA',
        headerName: 'QA',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['QA']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.QA': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'SEO',
        headerName: 'SEO',
        width: 55,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['SEO']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.SEO': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'other',
        headerName: 'other',
        width: 85,
        editable: true,
        valueGetter: (_, row) => {
          return row?._resolvedTeam?.['other']?.map((user: User) => user.name).join(', ')
        },
        valueSetter: (value, row) => {
          return { ...row, 'team.other': value }
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        renderCell: (params: GridRenderCellParams) => (
          <TeamMemberCell params={params} densityStyles={densityStyles} onEditClick={handleEditClick} />
        ),
        renderEditCell: userEditCell({ users }, true),
      },
      {
        field: 'creation_timestamp',
        headerName: 'Created Time',
        width: 75,
        headerAlign: 'center',
        valueGetter: (_value, row) => {
          const fallbackTimestamp = row?.SK?.split('_')?.[2] ?? null

          return row?.creation_timestamp || fallbackTimestamp || null
        },
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        renderCell: (params: GridRenderCellParams) => {
          if (params?.row?.isAggregationRow) return null
          return <DateTimeCell params={params} densityStyles={densityStyles} />
        },
      },
    ]
    return columns
  }
  return { organizationGridColumns }
}
